import TagId from '~/constants/TagId';
import { shortWeekDays } from '~/constants/weekDays';
import { useSearchRequestId } from '~/contexts/InventoryContext';
import { TileData } from '~/contexts/TileDataContext';
import type { AvailabilityInfo, Vehicle } from '~/contracts';
import usePriceDrop from '~/hooks/usePriceDrop';
import JSCookie from '~/utilities/BrowserCookies';
import {
  defaultSoonestAvailable,
  TWO_HOURS,
  months,
  daysWithUniqueFormat,
  DaysRange
} from '../constants';
import { InventoryTypeId, VehicleTag, PartialAvailabilityInfo } from '../types';

export function useVehicleHref(vehicle?: Partial<TileData>) {
  return vehicle ? `/vehicle/${vehicle.vehicleId}` : undefined;
}

export const useSetSearchRequestIdCookie = () => {
  const searchRequestId = useSearchRequestId();

  return () => {
    JSCookie.set('searchRequestId', searchRequestId);
  };
};

export const findGreatDeal = (tag: Partial<VehicleTag> = {}) =>
  tag?.tagId === TagId.GreatDeal;

export const findCleanTaxCredit = (tag: Partial<VehicleTag> = {}) =>
  tag?.tagId === TagId.EVCredit;

export const hasGreatDealTag = (tileData: Partial<TileData>) => {
  const { vehicleTags = [] } = tileData;
  const hasTag = vehicleTags.length > 0;

  return hasTag && Boolean(vehicleTags.find(findGreatDeal));
};

export const hasCleanTaxCredit = (tileData: Partial<TileData>) => {
  const { vehicleTags = [] } = tileData;
  const hasTag = vehicleTags.length > 0;

  return hasTag && Boolean(vehicleTags.find(findCleanTaxCredit));
};

export const useHasDealTag = (tileData: Partial<TileData>) => {
  const { getPriceDrop } = usePriceDrop();
  const showEvTag = hasCleanTaxCredit(tileData);
  const showGreatDealTag = hasGreatDealTag(tileData);
  const { showPriceDrop } = getPriceDrop(tileData);

  return showEvTag || showGreatDealTag || showPriceDrop;
};

export const useGreatDealTag = (
  tileData: Partial<TileData>
): VehicleTag | null => {
  return tileData?.vehicleTags?.find(findGreatDeal) ?? null;
};

export const calculateSoonestAvailable = (
  pickup?: AvailabilityInfo,
  delivery?: AvailabilityInfo
): PartialAvailabilityInfo => {
  if (pickup == null) return delivery ?? defaultSoonestAvailable;

  if (delivery == null) return pickup;

  if (pickup.drivingMiles > 150) return delivery;

  return delivery.daysToAvailability < pickup.daysToAvailability
    ? delivery
    : pickup;
};

export const getDeliveryOrPickupInfo = (vehicle: Partial<Vehicle>) => {
  // * meetupInfo is stored within deliveryInfo when isMeetupAllowed = true
  // * can to distinguish between meetup and delivery information by the activityType field
  //    * meetup: "MerchMeetup"
  //    * delivery: "LocalDelivery"
  const { pickupInfo, deliveryInfo } = vehicle;

  const soonestAvailable = calculateSoonestAvailable(pickupInfo, deliveryInfo);

  return {
    activityType: soonestAvailable?.activityType,
    dateAvailable: soonestAvailable?.utcSoonestDateTimeIso,
    dayAvailable: soonestAvailable?.asSoonAsText,
    daysTillDeliveryOrPickup: soonestAvailable?.daysToAvailability,
    locationCity: soonestAvailable?.locationCity,
    locationState: soonestAvailable?.locationState,
    routeId: soonestAvailable?.routeId
  };
};

export function getDaysRange(daysUntilDeliveryOrPickup: number) {
  if (daysUntilDeliveryOrPickup < 1) return DaysRange.SameDayDelivery;
  if (daysUntilDeliveryOrPickup === 1) return DaysRange.Tomorrow;
  if (daysUntilDeliveryOrPickup > 1 && daysUntilDeliveryOrPickup <= 4)
    return DaysRange.UnderFourDays;
  if (daysUntilDeliveryOrPickup > 4) return DaysRange.OverFourDays;

  return DaysRange.Unknown;
}

export const formatDateAndMonth = (
  date: string,
  truncateMonth = true
): string => {
  const dateFromString = new Date(date);
  const month = truncateMonth
    ? months[dateFromString.getMonth()].slice(0, 3)
    : months[dateFromString.getMonth()];
  const weekDay = shortWeekDays[dateFromString.getDay()];
  const day = dateFromString.getDate().toString();
  const dayLength = day.length;
  const requiresSpecialFormatting =
    Object.keys(daysWithUniqueFormat).includes(day[dayLength - 1]) &&
    (dayLength === 1 || (dayLength === 2 && day[0] !== '1'));
  const formattedDay = requiresSpecialFormatting
    ? day + daysWithUniqueFormat[day[dayLength - 1]]
    : `${day}th`;

  return `${weekDay}, ${month} ${formattedDay}`;
};

export function getDeliveryOrPickupByVehicle(tileData: Partial<TileData>): {
  activityType: string;
  dateAvailable: string;
  getItBy: string;
  routeId: string;
} {
  const {
    soonestAvailabilityInfo: {
      activityType = '',
      dateAvailable,
      dayAvailable,
      daysTillDeliveryOrPickup,
      routeId = ''
    } = {}
  } = tileData;

  if (!dateAvailable || daysTillDeliveryOrPickup === undefined)
    return { activityType: '', dateAvailable: '', getItBy: '', routeId: '' };

  const chipTextMap = {
    [DaysRange.SameDayDelivery]: 'Today',
    [DaysRange.UnderFourDays]: dayAvailable,
    [DaysRange.Tomorrow]: 'Tomorrow',
    [DaysRange.OverFourDays]: formatDateAndMonth(dateAvailable),
    [DaysRange.Unknown]: ''
  };

  return {
    activityType,
    dateAvailable,
    getItBy: chipTextMap[getDaysRange(daysTillDeliveryOrPickup)] ?? '',
    routeId
  };
}

export const getPurchaseStatus = (
  tileData: Partial<TileData>
): { isLocked: boolean; isOnHold: boolean } => {
  if (tileData?.vehicleUnlockDateTime == null) {
    return {
      isLocked: false,
      isOnHold: false
    };
  }

  const timeLeft = Date.parse(tileData?.vehicleUnlockDateTime) - +new Date();

  return {
    isLocked: Math.floor(timeLeft / 1000) >= TWO_HOURS,
    isOnHold: Math.floor(timeLeft / 1000) < TWO_HOURS
  };
};

export const getIsPreOrder = (tileData: Partial<TileData>) => {
  const { isLocked, isOnHold } = getPurchaseStatus(tileData);

  return isLocked || isOnHold
    ? false
    : tileData?.vehicleInventoryType === InventoryTypeId.WIP;
};

export const getVehicleAvailability = (tileData: Partial<TileData>) => {
  const { isOnDemand = false, vehiclePurchaseType = '' } = tileData;
  const isReservable = vehiclePurchaseType === 'Reservable';

  return Boolean(!isOnDemand && !isReservable);
};
